import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  ACCOUNT_STATUSES_BLOCKING_ORDERS,
  AccountStatus,
  CurrencyCode,
  createOrderSchema,
  formatMoney,
  type Customer,
  type Sku,
} from '@lombok/shared';
import { ApiError, type CreateOrderResult } from '@lombok/api-client';
import { Button, Card, CardContent, CardHeader, CardTitle } from '@lombok/ui';
import { accountStatusText } from '@/features/accounts/accountActions';
import { PageHeader } from '@/components/PageHeader';
import { FORM_COLUMN, WIDE_COLUMN } from '@/components/layout';
import { BackLink } from '@/components/DetailRow';
import { StatusPill } from '@/components/StatusPill';
import { FormError, TextField, TextareaField } from '@/components/form';
import { useCreateOrder, useCustomer, useExchangeRate, useProductCatalog } from '@/data/hooks';
import { formatDecimalInput, parseDecimal } from '@/lib/decimal';
import { CustomerPicker } from './CustomerPicker';
import { OrderLineRow, type DraftLine } from './OrderLineRow';
import { useOrderCart } from './useOrderCart';
import type { RepeatOrderNotice } from './useRepeatOrder';
import { cartSubtotalMinorUsd, cartSubtotalMinorVes, type CartLine } from './cart';
import { orderLabel } from '@/lib/names';
import { approvalReasonLabel } from './labels';

interface OrderCreatePageProps {
  /** Group root used for the back link + the success "Ver pedido" detail link. */
  basePath: string;
  /** Where the orders list lives under this group (for the back link). */
  ordersPath: string;
  /**
   * Customer portal: the order is always for this account (no customer picker) and is locked at the
   * active exchange rate, so the rate field is not shown. The API enforces both rules as well.
   */
  fixedCustomerId?: string;
  /**
   * Distributor portal: the customer is one of the distributor's sub-accounts (picked from the list the API already
   * limits to them). As for a customer, the order is locked at the active rate, so the rate field is not shown.
   */
  lockedRate?: boolean;
  /** Back link text (defaults to "Pedidos"). */
  backLabel?: string;
}

let lineSeq = 0;
const newLine = (): DraftLine => ({ key: `l${++lineSeq}`, productId: '', skuId: '', quantity: 1 });

/** A fresh idempotency key per order attempt (retry-safe; rotated after success). */
function newIdempotencyKey(): string {
  const c = globalThis.crypto;
  if (c && typeof c.randomUUID === 'function') return `order-${c.randomUUID()}`;
  return `order-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

/**
 * Crear pedido. A sales rep / admin: (1) picks a customer, (2) adds line items
 * (product → SKU → quantity), (3) sets the manual exchange rate (VES per USD)
 * and optional notes, sees the live USD subtotal + VES equivalent, then submits.
 * The exchange rate is LOCKED server-side at creation. Validation uses the shared
 * `createOrderSchema`; the backend remains authoritative on price and approval.
 */
export function OrderCreatePage({
  basePath,
  ordersPath,
  fixedCustomerId,
  lockedRate = false,
  backLabel = 'Pedidos',
}: OrderCreatePageProps) {
  const navigate = useNavigate();
  // Set when the lines came from "Repetir pedido": tells the customer what was loaded and what was left out.
  const [noticeDismissed, setNoticeDismissed] = useState(false);
  const repeatNotice = noticeDismissed ? undefined : ((useLocation().state as RepeatOrderNotice | null) ?? undefined);
  const productsQuery = useProductCatalog();
  const products = useMemo(
    () => (productsQuery.data?.items ?? []).filter((p) => p.active),
    [productsQuery.data],
  );
  const mutation = useCreateOrder();
  const availabilityByProduct = useMemo(
    () => new Map((productsQuery.data?.items ?? []).map((p) => [p.productId, p.availability] as const)),
    [productsQuery.data],
  );

  const [idemKey, setIdemKey] = useState(newIdempotencyKey);
  const [picked, setPicked] = useState<Customer | null>(null);
  const ownAccount = useCustomer(fixedCustomerId);
  const customer = fixedCustomerId ? (ownAccount.data ?? null) : picked;
  const isOwnAccount = Boolean(fixedCustomerId);
  const forSubaccount = lockedRate && !isOwnAccount;
  // The server locks the rate (and the screen shows availability labels) for customers and distributors alike.
  const rateLocked = isOwnAccount || lockedRate;
  // A customer's order in progress (from the catalog) is the starting point; it is kept in sync below.
  const orderCart = useOrderCart(fixedCustomerId);
  const [lines, setLines] = useState<DraftLine[]>(() =>
    orderCart.items.length > 0
      ? orderCart.items.map((i) => ({ ...newLine(), productId: i.productId, skuId: i.skuId, quantity: i.quantity }))
      : [newLine()],
  );
  const { replace: replaceCart, clear: clearCart } = orderCart;
  useEffect(() => {
    if (!fixedCustomerId) return;
    replaceCart(
      lines
        .filter((l) => l.productId && l.skuId && l.quantity >= 1)
        .map((l) => ({ productId: l.productId, skuId: l.skuId, quantity: Math.trunc(l.quantity) })),
    );
  }, [fixedCustomerId, lines, replaceCart]);
  const [rateText, setRateText] = useState('');
  // Until the user edits the field, follow the active rate from Configuración. The server
  // still locks whatever is submitted; this only avoids retyping it (and typos).
  const [rateTouched, setRateTouched] = useState(false);
  const activeRate = useExchangeRate().data?.rate;
  useEffect(() => {
    if (!rateTouched && activeRate) setRateText(formatDecimalInput(activeRate));
  }, [activeRate, rateTouched]);
  const [notes, setNotes] = useState('');
  // Resolved SKU per line key (reported by each row's query) for live pricing.
  const [skuByLine, setSkuByLine] = useState<Record<string, Sku | undefined>>({});
  const [result, setResult] = useState<CreateOrderResult | null>(null);
  const [formError, setFormError] = useState<string | null>(null);

  const onSkuResolved = useCallback((key: string, sku: Sku | undefined) => {
    setSkuByLine((prev) => (prev[key] === sku ? prev : { ...prev, [key]: sku }));
  }, []);

  const patchLine = (key: string, patch: Partial<Omit<DraftLine, 'key'>>) =>
    setLines((prev) => prev.map((l) => (l.key === key ? { ...l, ...patch } : l)));

  // Empties the customer's order in progress (also the one kept from the catalog) and leaves one blank line.
  const clearOrder = () => {
    setLines([newLine()]);
    setSkuByLine({});
    setNoticeDismissed(true);
    clearCart();
  };

  const removeLine = (key: string) => {
    setLines((prev) => prev.filter((l) => l.key !== key));
    setSkuByLine((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  // A customer's or distributor's order is locked at the active rate (the server ignores any other), so use it as is.
  const rate = rateLocked ? (activeRate ?? Number.NaN) : parseDecimal(rateText);
  const rateValid = Number.isFinite(rate) && rate > 0;

  // Build the priced cart from lines whose SKU resolved + quantity > 0.
  const cart: CartLine[] = useMemo(
    () =>
      lines
        .map((l) => ({ sku: skuByLine[l.key], quantity: l.quantity }))
        .filter((c): c is CartLine => Boolean(c.sku) && c.quantity > 0),
    [lines, skuByLine],
  );
  const subtotalUsd = cartSubtotalMinorUsd(cart);
  const subtotalVes = cartSubtotalMinorVes(cart, rate);

  // Submittable lines: valid skuId + positive quantity (what goes to the API).
  const orderItems = useMemo(
    () =>
      lines
        .filter((l) => l.skuId && l.quantity > 0)
        .map((l) => ({ skuId: l.skuId, quantity: l.quantity })),
    [lines],
  );

  const canSubmit =
    Boolean(customer) &&
    !(customer && ACCOUNT_STATUSES_BLOCKING_ORDERS.includes(customer.status)) &&
    rateValid &&
    orderItems.length > 0 &&
    !mutation.isPending;

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);
    if (!customer) {
      setFormError(isOwnAccount ? 'No se pudo cargar tu cuenta. Recarga la página.' : 'Selecciona un cliente.');
      return;
    }
    const candidate = {
      customerId: customer.customerId,
      currency: CurrencyCode.USD,
      exchangeRate: rate,
      items: orderItems,
      notes: notes.trim() || undefined,
      idempotencyKey: idemKey,
    };
    const parsed = createOrderSchema.safeParse(candidate);
    if (!parsed.success) {
      setFormError(parsed.error.issues[0]?.message ?? 'Revisa los datos del pedido.');
      return;
    }
    mutation.mutate(parsed.data, {
      onSuccess: (created) => {
        setResult(created);
        clearCart();
      },
      // The server answered, so nothing was created: the next attempt needs a fresh key (a reused
      // one could be reported as already processed). A network failure keeps the key on purpose.
      onError: (error) => {
        if (error instanceof ApiError) setIdemKey(newIdempotencyKey());
      },
    });
  };

  // ---- Success panel (after createOrder) ----
  if (result) {
    return (
      <div className={`flex flex-col gap-6 ${FORM_COLUMN}`}>
        <div className="flex flex-col gap-2">
          <BackLink to={ordersPath}>{backLabel}</BackLink>
          <PageHeader title="Pedido creado" description="El pedido se registró correctamente." />
        </div>
        <Card>
          <CardContent className="flex flex-col gap-4 pt-5">
            <div className="flex items-center justify-between gap-3">
              <div>
                <p className="text-sm text-text-muted">Número de pedido</p>
                <p className="font-mono text-sm font-medium text-text-primary">{orderLabel(result)}</p>
              </div>
              <StatusPill kind="order" value={result.status} />
            </div>

            {result.requiresApproval ? (
              <div className="rounded-md border border-warning-soft bg-warning-soft/50 p-3">
                <p className="text-sm font-medium text-text-primary">
                  {isOwnAccount
                    ? 'Tu pedido quedó pendiente de aprobación.'
                    : 'Este pedido requiere aprobación antes de continuar.'}
                </p>
                {result.approvalReasons.length > 0 ? (
                  <ul className="mt-2 list-inside list-disc text-sm text-text-muted">
                    {result.approvalReasons.map((code) => (
                      <li key={code}>{approvalReasonLabel(code, isOwnAccount)}</li>
                    ))}
                  </ul>
                ) : null}
              </div>
            ) : (
              <p className="text-sm text-success">
                {isOwnAccount ? 'Tu pedido fue enviado y no requiere aprobación.' : 'El pedido no requiere aprobación.'}
              </p>
            )}

            <div className="flex flex-col-reverse gap-2 border-t border-border pt-4 sm:flex-row sm:justify-end">
              <Button
                variant="outline"
                onClick={() => {
                  // Reset for a fresh order (new idempotency key so it isn't a replay).
                  setResult(null);
                  setPicked(null);
                  setLines([newLine()]);
                  setSkuByLine({});
                  setNoticeDismissed(true);
                  setRateText('');
                  setNotes('');
                  setIdemKey(newIdempotencyKey());
                }}
              >
                Crear otro pedido
              </Button>
              <Link to={`${basePath}/${result.orderId}`}>
                <Button className="w-full sm:w-auto">Ver pedido</Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // ---- Create form ----
  return (
    <div className={`flex flex-col gap-6 ${WIDE_COLUMN}`}>
      <div className="flex flex-col gap-2">
        <BackLink to={ordersPath}>{backLabel}</BackLink>
        <PageHeader
          title="Crear pedido"
          description={
            isOwnAccount
              ? 'Arma tu pedido con los precios de hoy.'
              : forSubaccount
                ? 'Arma un pedido para uno de tus subclientes con los precios de hoy.'
                : 'Arma un pedido para un cliente.'
          }
        />
      </div>

      <form onSubmit={onSubmit} className="flex flex-col gap-6" noValidate>
        {/* 1. Cliente */}
        <Card>
          <CardHeader>
            <CardTitle>{isOwnAccount ? '1. Tu cuenta' : forSubaccount ? '1. Subcliente' : '1. Cliente'}</CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            {customer ? (
              <div className="flex flex-col gap-3 rounded-md border border-border p-3 sm:flex-row sm:items-center sm:justify-between">
                <div className="flex flex-col gap-1">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-text-primary">{customer.name}</span>
                    <StatusPill kind="account" value={customer.status} />
                  </div>
                  <div className="flex flex-wrap gap-x-4 text-xs text-text-muted">
                    <span>{customer.rif}</span>
                    <span>Saldo: {formatMoney(customer.openBalanceMinorUsd)}</span>
                    <span>Crédito disp.: {formatMoney(customer.availableCreditMinorUsd)}</span>
                  </div>
                </div>
                {isOwnAccount ? null : (
                  <Button type="button" variant="outline" size="sm" onClick={() => setPicked(null)}>
                    Cambiar
                  </Button>
                )}
              </div>
            ) : isOwnAccount ? (
              <p className="text-sm text-text-muted" role={ownAccount.isError ? 'alert' : 'status'}>
                {ownAccount.isError ? 'No se pudo cargar tu cuenta. Recarga la página.' : 'Cargando tu cuenta…'}
              </p>
            ) : (
              <CustomerPicker
                selectedId=""
                onSelect={setPicked}
                emptyDescription={
                  forSubaccount ? 'Aún no tienes subclientes. Un administrador puede vincularlos a tu cuenta.' : undefined
                }
              />
            )}
            {customer && ACCOUNT_STATUSES_BLOCKING_ORDERS.includes(customer.status) ? (
              <p
                role="alert"
                className="mt-3 rounded-md border border-danger-soft bg-danger-soft p-3 text-sm font-medium text-danger"
              >
                {isOwnAccount
                  ? `Tu cuenta está ${accountStatusText(customer.status)}: no puedes crear pedidos por ahora. Contacta a tu vendedor para reactivarla.`
                  : `La cuenta de este cliente está ${accountStatusText(customer.status)}: no se pueden crear pedidos hasta que un administrador la reactive.`}
              </p>
            ) : null}
            {customer && customer.status === AccountStatus.PENDING ? (
              <p
                role="status"
                className="mt-3 rounded-md border border-warning-soft bg-warning-soft p-3 text-sm text-text-primary"
              >
                {isOwnAccount ? 'Tu cuenta está en revisión.' : 'La cuenta de este cliente está en revisión.'} Puedes
                crear el pedido, pero quedará pendiente de aprobación hasta que se apruebe la cuenta.
              </p>
            ) : null}
          </CardContent>
        </Card>

        {/* 2. Productos */}
        <Card>
          <CardHeader>
            <CardTitle>2. Productos</CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col gap-3 pt-0">
            {isOwnAccount && repeatNotice?.repeatedFrom ? (
              <div role="status" className="rounded-md border border-border bg-surface-muted p-3 text-sm text-text-primary">
                <p>
                  Cargamos las líneas de {repeatNotice.repeatedFrom} con los precios y la disponibilidad de hoy.
                  Revisa todo antes de enviar.
                </p>
                {repeatNotice.skipped?.length ? (
                  <p className="mt-2 text-text-muted">
                    Ya no están disponibles y no se agregaron: {repeatNotice.skipped.join(', ')}.
                  </p>
                ) : null}
              </div>
            ) : null}
            {productsQuery.isError ? (
              <p className="text-sm text-danger">No se pudo cargar el catálogo. Recarga la página.</p>
            ) : null}
            {lines.map((line, index) => (
              <OrderLineRow
                key={line.key}
                index={index}
                line={line}
                products={products}
                availability={rateLocked ? availabilityByProduct.get(line.productId) : undefined}
                onChange={(patch) => patchLine(line.key, patch)}
                onRemove={() => removeLine(line.key)}
                onSkuResolved={onSkuResolved}
                removable={lines.length > 1}
              />
            ))}
            <div>
              <Button type="button" variant="outline" size="sm" onClick={() => setLines((p) => [...p, newLine()])}>
                Agregar línea
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* 3. Tasa, notas y total */}
        <Card>
          <CardHeader>
            <CardTitle>3. Tasa y total</CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col gap-5 pt-0">
            {rateLocked ? (
              <p className="text-sm text-text-muted">
                {activeRate
                  ? `Tasa del día: ${formatDecimalInput(activeRate)} VES por USD. Se fija en ${isOwnAccount ? 'tu' : 'el'} pedido al crearlo y no se recalcula.`
                  : 'Aún no hay una tasa de cambio activa, por eso no se puede crear el pedido. Intenta más tarde.'}
              </p>
            ) : (
            <div className="grid gap-5 sm:grid-cols-2">
              <TextField
                label="Tasa de cambio (VES por USD)"
                inputMode="decimal"
                required
                placeholder="Ej. 40,00"
                hint={
                  activeRate
                    ? 'Precargada con la tasa activa. Se fija en el pedido al crearlo y no se recalcula.'
                    : 'Se fija en el pedido al crearlo y no se recalcula.'
                }
                value={rateText}
                onChange={(e) => {
                  setRateTouched(true);
                  setRateText(e.target.value);
                }}
              />
            </div>
            )}
            <TextareaField
              label="Notas"
              placeholder="Indicaciones opcionales para este pedido."
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />

            <div className="flex flex-col gap-2 rounded-md bg-surface-muted p-4">
              <div className="flex items-center justify-between">
                <span className="text-sm text-text-muted">Total (USD)</span>
                <span className="text-2xl font-semibold tracking-tight text-text-primary">
                  {formatMoney(subtotalUsd)}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-text-muted">Equivalente (VES)</span>
                <span className="text-base font-medium text-text-primary">
                  {rateValid ? formatMoney(subtotalVes, CurrencyCode.VES) : '—'}
                </span>
              </div>
              {!rateValid && !rateLocked ? (
                <p className="text-xs text-text-muted">
                  Ingresa una tasa válida para ver el equivalente en VES.
                </p>
              ) : null}
            </div>

            <FormError error={mutation.error} />
            {formError ? (
              <p className="text-sm text-danger" role="alert">
                {formError}
              </p>
            ) : null}

            <div className="flex flex-col-reverse gap-2 border-t border-border pt-4 sm:flex-row sm:justify-end">
              {isOwnAccount ? (
                <Button type="button" variant="outline" className="sm:mr-auto" onClick={clearOrder}>
                  Vaciar pedido
                </Button>
              ) : null}
              <Button type="button" variant="outline" onClick={() => navigate(ordersPath)}>
                Cancelar
              </Button>
              <Button type="submit" disabled={!canSubmit}>
                {mutation.isPending ? 'Creando…' : 'Crear pedido'}
              </Button>
            </div>
          </CardContent>
        </Card>
      </form>
    </div>
  );
}
